import React from "react";
import { Icon } from "./Icon";
import { PageMeta } from "./PageMeta";

interface LoginProps {
  onLogin: () => void;
}

export const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#0a0a0a",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <PageMeta
        title="Log in — HAL 8580"
        description="Connect your Twitch account to HAL 8580 and add an AI companion to your stream chat in about two minutes."
        path="/login"
        breadcrumbs={[{ name: "Log in" }]}
      />
      {/* Left side - HAL Image */}
      <div
        style={{
          flex: isMobile ? "0 0 auto" : "0 0 40%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? "20px" : "40px",
          minHeight: "auto",
        }}
      >
        <img
          src="/assets/hal-8580-computer.jpg"
          alt="HAL 8580"
          style={{
            width: isMobile ? "200px" : "auto",
            maxWidth: "90%",
            height: isMobile ? "auto" : "600px",
            minHeight: isMobile ? "auto" : "500px",
            maxHeight: "80vh",
            objectFit: "contain",
            borderRadius: "0",
            boxShadow: "0 0 80px 20px rgba(145, 70, 255, 0.4)",
          }}
        />
      </div>

      {/* Right side - Content */}
      <div
        style={{
          flex: "1",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: isMobile ? "20px" : "60px 80px",
          maxWidth: isMobile ? "100%" : "600px",
        }}
      >
        {/* Title */}
        <h1
          style={{
            fontSize: isMobile ? "48px" : "72px",
            fontWeight: "700",
            background: "linear-gradient(135deg, #9146FF 0%, #00D4FF 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            margin: "0 0 16px 0",
            letterSpacing: "-2px",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          HAL 8580
        </h1>

        {/* Description */}
        <p
          style={{
            fontSize: isMobile ? "18px" : "24px",
            color: "#a0aec0",
            marginBottom: isMobile ? "32px" : "48px",
            lineHeight: "1.4",
            fontWeight: "300",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          The most intelligent chat bot for your Twitch streams
        </p>

        {/* Features */}
        <div style={{ marginBottom: isMobile ? "32px" : "48px" }}>
          <div
            style={{
              display: "grid",
              gap: isMobile ? "16px" : "24px",
              marginBottom: "32px",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}
            >
              <div
                style={{ color: "#9146FF", marginTop: "2px", display: "flex" }}
              >
                <Icon name="users-round" size={22} />
              </div>
              <div>
                <h3
                  style={{
                    color: "#e2e8f0",
                    margin: "0 0 4px 0",
                    fontSize: isMobile ? "16px" : "18px",
                    fontWeight: "600",
                  }}
                >
                  Dynamic Personalities
                </h3>
                <p
                  style={{
                    color: "#cbd5e0",
                    margin: 0,
                    fontSize: isMobile ? "12px" : "14px",
                    lineHeight: "1.5",
                  }}
                >
                  Choose from dozens of AI personalities - DJ, Musician, Gamer,
                  Tech Expert, and more
                </p>
              </div>
            </div>

            <div
              style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}
            >
              <div
                style={{ color: "#9146FF", marginTop: "2px", display: "flex" }}
              >
                <Icon name="brain" size={22} />
              </div>
              <div>
                <h3
                  style={{
                    color: "#e2e8f0",
                    margin: "0 0 4px 0",
                    fontSize: isMobile ? "16px" : "18px",
                    fontWeight: "600",
                  }}
                >
                  Advanced Memory System
                </h3>
                <p
                  style={{
                    color: "#cbd5e0",
                    margin: 0,
                    fontSize: isMobile ? "12px" : "14px",
                    lineHeight: "1.5",
                  }}
                >
                  Persistent conversation threads, sentiment analysis, and trend
                  detection across all interactions
                </p>
              </div>
            </div>

            <div
              style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}
            >
              <div
                style={{ color: "#9146FF", marginTop: "2px", display: "flex" }}
              >
                <Icon name="zap" size={22} />
              </div>
              <div>
                <h3
                  style={{
                    color: "#e2e8f0",
                    margin: "0 0 4px 0",
                    fontSize: isMobile ? "16px" : "18px",
                    fontWeight: "600",
                  }}
                >
                  Real-time Context API
                </h3>
                <p
                  style={{
                    color: "#cbd5e0",
                    margin: 0,
                    fontSize: isMobile ? "12px" : "14px",
                    lineHeight: "1.5",
                  }}
                >
                  Feed live data - current song, BPM, game title, weather, or
                  any custom context
                </p>
              </div>
            </div>

            <div
              style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}
            >
              <div
                style={{ color: "#9146FF", marginTop: "2px", display: "flex" }}
              >
                <Icon name="bar-chart-3" size={22} />
              </div>
              <div>
                <h3
                  style={{
                    color: "#e2e8f0",
                    margin: "0 0 4px 0",
                    fontSize: isMobile ? "16px" : "18px",
                    fontWeight: "600",
                  }}
                >
                  Analytics & Triggers
                </h3>
                <p
                  style={{
                    color: "#cbd5e0",
                    margin: 0,
                    fontSize: isMobile ? "12px" : "14px",
                    lineHeight: "1.5",
                  }}
                >
                  Audience insights, automated responses, and intelligent
                  conversation management
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Login Button */}
        <button
          onClick={onLogin}
          style={{
            background: "linear-gradient(135deg, #9146FF 0%, #00D4FF 100%)",
            color: "white",
            border: "none",
            padding: isMobile ? "14px 28px" : "16px 32px",
            fontSize: isMobile ? "16px" : "18px",
            fontWeight: "600",
            borderRadius: "12px",
            cursor: "pointer",
            transition: "all 0.3s ease",
            boxShadow: "0 8px 25px rgba(145, 70, 255, 0.4)",
            width: isMobile ? "100%" : "fit-content",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow =
              "0 12px 35px rgba(145, 70, 255, 0.6)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 8px 25px rgba(145, 70, 255, 0.4)";
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Icon name="gamepad-2" size={20} color="white" />
            Login with Twitch
          </span>
        </button>

        {/* Subtle footer text */}
        <p
          style={{
            color: "#4a5568",
            fontSize: "12px",
            marginTop: isMobile ? "24px" : "32px",
            fontWeight: "300",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          Connect your Twitch account to unlock the future of stream interaction
        </p>
      </div>
    </div>
  );
};
